import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

/**
 * Secure storage service using Capacitor Preferences API
 * 
 * Provides encrypted storage for sensitive data on native platforms
 * Falls back to localStorage in browser but with consistent API
 * 
 * Key security improvements:
 * - Native encrypted storage on iOS/Android
 * - Consistent error handling
 * - Type-safe methods for common data types
 * - Automatic JSON serialization/deserialization
 */
@Injectable({
  providedIn: 'root'
})
export class SecureStorageService {

  /**
   * Store a string value
   */
  async setItem(key: string, value: string): Promise<void> {
    try {
      await Preferences.set({ key, value });
    } catch (error) {
      console.error(`Error storing item with key "${key}":`, error);
      throw error;
    }
  }

  /**
   * Retrieve a string value
   */
  async getItem(key: string): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key });
      return value;
    } catch (error) {
      console.error(`Error retrieving item with key "${key}":`, error);
      return null;
    }
  }

  /**
   * Remove a specific item
   */
  async removeItem(key: string): Promise<void> {
    try {
      await Preferences.remove({ key });
    } catch (error) {
      console.error(`Error removing item with key "${key}":`, error);
      throw error;
    }
  }

  /**
   * Clear all stored data
   */
  async clear(): Promise<void> {
    try {
      await Preferences.clear();
    } catch (error) {
      console.error('Error clearing all storage:', error);
      throw error;
    }
  }

  /**
   * Store an object (automatically serialized to JSON)
   */
  async setObject(key: string, value: unknown): Promise<void> {
    try {
      const jsonValue = JSON.stringify(value);
      await this.setItem(key, jsonValue);
    } catch (error) {
      console.error(`Error storing object with key "${key}":`, error);
      throw error;
    }
  }

  /**
   * Retrieve an object (automatically deserialized from JSON)
   * Returns null if key doesn't exist or JSON is invalid
   */
  async getObject<T = unknown>(key: string): Promise<T | null> {
    try {
      const value = await this.getItem(key);
      if (!value) {
        return null;
      }
      
      try {
        const parsed: T = JSON.parse(value);
        return parsed;
      } catch (parseError) {
        console.error(`Error parsing JSON for key "${key}":`, parseError);
        return null;
      }
    } catch (error) {
      console.error(`Error retrieving object with key "${key}":`, error);
      return null;
    }
  }

  /**
   * Check if a key exists
   */
  async hasItem(key: string): Promise<boolean> {
    const value = await this.getItem(key);
    return value !== null;
  }

  /**
   * Get all keys currently stored
   */
  async keys(): Promise<string[]> {
    try {
      const { keys } = await Preferences.keys();
      return keys;
    } catch (error) {
      console.error('Error retrieving keys:', error);
      return [];
    }
  }
}
